import React from 'react';

interface UpgradeSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  oldPlan: string;
  newPlan: string;
}

const UpgradeSuccessModal: React.FC<UpgradeSuccessModalProps> = ({
  isOpen,
  onClose,
  oldPlan,
  newPlan,
}) => {
  if (!isOpen) return null;

  // Determine if this is an upgrade, downgrade, or resubscribe
  const isResubscribe = oldPlan.toLowerCase() === 'cancelled';
  
  const planHierarchy: Record<string, number> = {
    'Free': 0, 'free': 0,
    'Basic Plan': 1, 'Essential': 1, 'Gold': 1, 'essential': 1,
    'Plus': 2, 'plus': 2,
    'Premium': 3, 'premium': 3,
    'Cancelled': -1, // Special case for resubscribe
  };

  const oldLevel = planHierarchy[oldPlan] || 1;
  const newLevel = planHierarchy[newPlan] || 1;
  const isDowngrade = !isResubscribe && newLevel < oldLevel;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-slate-800/95 backdrop-blur-xl rounded-3xl p-8 max-w-md w-full border border-slate-700/50 shadow-2xl animate-in fade-in zoom-in duration-300">
        <div className="flex flex-col items-center">
          {/* Success Icon */}
          <div className={`w-20 h-20 bg-gradient-to-br ${isDowngrade ? 'from-orange-500/20 to-amber-500/20' : 'from-green-500/20 to-emerald-500/20'} rounded-full flex items-center justify-center mb-6 ring-4 ${isDowngrade ? 'ring-orange-500/20' : 'ring-green-500/20'}`}>
            <svg 
              className={`w-10 h-10 ${isDowngrade ? 'text-orange-500' : 'text-green-500'}`}
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2.5} 
                d="M5 13l4 4L19 7" 
              />
            </svg>
          </div>

          {/* Title */}
          <h2 className="text-3xl font-bold text-white mb-3 text-center">
            {isResubscribe ? 'Subscription Reactivated!' : isDowngrade ? 'Downgrade Successful!' : 'Upgrade Successful!'}
          </h2>

          {/* Description */}
          <div className="bg-slate-700/30 rounded-xl p-4 mb-6 w-full">
            <p className="text-slate-300 text-center text-sm mb-2">
              {isResubscribe 
                ? `Your ${newPlan} subscription has been reactivated`
                : `Your subscription has been ${isDowngrade ? 'downgraded' : 'upgraded'} from`}
            </p>
            {!isResubscribe && (
              <div className="flex items-center justify-center gap-3 mb-2">
                <span className="px-3 py-1 bg-slate-600/50 rounded-lg text-slate-400 font-medium text-sm">
                  {oldPlan}
                </span>
                <svg 
                  className={`w-5 h-5 ${isDowngrade ? 'text-orange-500' : 'text-green-500'}`}
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M13 7l5 5m0 0l-5 5m5-5H6" 
                  />
                </svg>
                <span className={`px-3 py-1 bg-gradient-to-r ${isDowngrade ? 'from-orange-600 to-amber-600' : 'from-green-600 to-emerald-600'} rounded-lg text-white font-semibold text-sm`}>
                  {newPlan}
                </span>
              </div>
            )}
            <p className="text-slate-400 text-center text-xs">
              {isResubscribe
                ? `Your ${newPlan} subscription will continue without interruption.`
                : isDowngrade 
                  ? `Your plan will change to ${newPlan} features at the end of your billing period.`
                  : `All ${newPlan} features are now available!`}
            </p>
          </div>

          {/* Billing Info */}
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 mb-6 w-full">
            <div className="flex items-start gap-3">
              <svg
                className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                {isResubscribe ? (
                  <>
                    <p className="text-sm text-slate-300">
                      Your subscription cancellation has been reversed.
                    </p>
                    <p className="text-sm text-slate-400 mt-1">
                      You'll continue to be billed normally for your {newPlan} subscription.
                    </p>
                  </>
                ) : isDowngrade ? (
                  <>
                    <p className="text-sm text-slate-300">
                      You'll keep access to {oldPlan} features until your current billing period ends.
                    </p>
                    <p className="text-sm text-slate-400 mt-1">
                      After that, you'll be on the {newPlan}.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-slate-300">
                      Your billing has been prorated automatically.
                    </p>
                    <p className="text-sm text-slate-400 mt-1">
                      You'll only pay for the difference between plans.
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Action Button */}
          <button
            onClick={onClose}
            className="w-full py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white rounded-xl font-semibold transition-all transform hover:scale-[1.02] active:scale-[0.98]"
          >
            Continue to TradeRewind
          </button>

          {/* Footer */}
          <p className="text-center text-xs text-slate-500 mt-4">
            {isResubscribe
              ? 'Welcome back! 🎉'
              : isDowngrade 
                ? 'Your plan change has been scheduled.'
                : 'Thank you for upgrading! 🎉'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default UpgradeSuccessModal;
